import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from '../../utils/axiosInstance';
import { toastError, toastSuccess, confirmDelete } from '../../utils/alertHelper';
import Select from 'react-select';
import { hasPermission } from '../../utils/permissionHelper';

const FamilyView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('business');
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planOptions, setPlanOptions] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planStartDate, setPlanStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [savingPlan, setSavingPlan] = useState(false);
  const [expiringPlanId, setExpiringPlanId] = useState(null);
  const [activity, setActivity] = useState({});
  const [activityTenure, setActivityTenure] = useState({});
  const [plan, setPlan] = useState(null);
  const [memberUsers, setMemberUsers] = useState([]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`/api/user/${id}`);
        const u = res.data.user;
        const p = res.data.purchasePlans;
        if (!u) throw new Error('User not found');
        setUser(u);
        setPlan(p);
        setActivity(res.data.Activity || {});
        setActivityTenure(res.data.activityTenure || {});
      } catch (err) {
        toastError('Failed to fetch user');
        navigate('/family');
      }
    };
    fetchUser();
  }, [id, navigate]);

  // Fetch all members for invitedByUser resolution
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const params = new URLSearchParams({
          userType: 'Member',
          page: 1,
          limit: 1000,
        });
        const res = await axios.get(`/api/user?${params.toString()}`);
        setMemberUsers(res.data.users || []);
      } catch (err) {
        setMemberUsers([]);
      }
    };
    fetchMembers();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return isNaN(date) ? '-' : date.toLocaleDateString();
  };

  const renderData = (data) => {
    if (!data) return '-';
    if (Array.isArray(data)) {
      if (data.length === 0) return '-';
      return data
        .map((item) => {
          if (!item) return '-';
          if (typeof item === 'string') return item;
          if (typeof item === 'object') {
            return item.name || item.label || item._id || JSON.stringify(item);
          }
          return String(item);
        })
        .join(', ');
    }
    if (typeof data === 'object') {
      return data.name || data.label || data._id || JSON.stringify(data);
    }
    return String(data);
  };

  useEffect(() => {
    if (activeTab === "attandance") {
      setLoading(true);
      axios
        .get("/api/attendance/all")
        .then((res) => {
          if (res.data && res.data.data) {
            const userAttendance = res.data.data.filter(
              (att) => att.member?._id === id
            );
            setAttendance(userAttendance);
          } else {
            setAttendance([]);
          }
        })
        .catch((err) => {
          toastError("Failed to fetch attendance");
          setAttendance([]);
        })
        .finally(() => setLoading(false));
    }
  }, [activeTab, id]);


  // Fetch plans when modal opens
  useEffect(() => {
    if (showPlanModal) {
      axios.get('/api/plan')
        .then(res => {
          setPlanOptions(res.data.plans?.map(p => ({ value: p._id, label: p.name })) || []);
        })
        .catch(() => setPlanOptions([]));
    }
  }, [showPlanModal]);

  const handleSavePlan = async () => {
    if (!selectedPlan) {
      toastError('Please select a plan');
      return;
    }
    setSavingPlan(true);
    try {
      await axios.post('/api/user/create-plan', {
        user: user._id,
        plan: selectedPlan.value,
        startDate: planStartDate || new Date().toISOString().split('T')[0],
      });
      toastSuccess('Plan purchased successfully!');
      setShowPlanModal(false);
      setSelectedPlan(null);
      setPlanStartDate(new Date().toISOString().split('T')[0]);
    } catch (err) {
      toastError('Failed to purchase plan');
    }
    setSavingPlan(false);
  };

  // Expire a specific purchased plan
  const handleExpirePlan = (planObj) => {
    if (!planObj || !planObj._id) return;
    // Show short "expiring" state in UI
    setExpiringPlanId(planObj._id);

    // Update local state only (no API integration) - mark endDate as now
    setPlan(prev =>
      Array.isArray(prev)
        ? prev.map(p => (p._id === planObj._id ? { ...p, endDate: new Date().toISOString() } : p))
        : prev
    );

    toastSuccess('Plan marked as expired (local only)');

    // Clear the expiring indicator after a brief moment
    setTimeout(() => setExpiringPlanId(null), 600);
  };

  // Helper to resolve invitedByUser to name
  const getInvitedByName = () => {
    if (!user) return '-';
    if (user.invitedByUser) {
      if (typeof user.invitedByUser === 'object') {
        if (user.invitedByUser.firstName)
          return `${user.invitedByUser.firstName} ${user.invitedByUser.lastName || ''}`;
        return user.invitedByUser.email || user.invitedByUser._id || '-';
      }
      if (typeof user.invitedByUser === 'string') {
        const member = memberUsers.find(m => m._id === user.invitedByUser);
        if (member) {
          return `${member.firstName} ${member.lastName || ''}`;
        }
        return user.invitedByUser;
      }
    }
    if (user.source === 'socialMedia') return 'Social Media';
    return user.source || '-';
  };

  if (!user) return <div className="text-center py-5">Loading user data...</div>;

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="text-dark border-bottom pb-2 mb-0">View User Details</h4>
        <Link
          to={`/family/edit/${user._id}`}
          className="btn btn-sm btn-success text-white"
        >
          <span>Edit</span>
        </Link>
      </div>
      <div className="row">
        {/* LEFT SIDE - Basic Info */}
        <div className="col-md-4">
          <div className="card shadow p-3">
            <h5 className="text-primary-custom mb-3">Basic Information</h5>
            {/* Profile Picture */}
            <div className="mb-3 text-center">
              <img
                src={
                  user.profilepic ||
                  user.profilePicture ||
                  "https://via.placeholder.com/120x120?text=No+Image"
                }
                alt="Profile"
                style={{ width: 120, height: 120, objectFit: "cover", borderRadius: "50%", border: "1px solid #ccc" }}
              />
            </div>
           
            <p><strong>Full Name:</strong> {`${user.firstName || '-'} ${user.middleName || ''} ${user.lastName || ''}`}</p>
            <p><strong>Email:</strong> {user.email || '-'}</p>
            <p><strong>Mobile:</strong> {user.mobileNumber || '-'}</p>
            <p><strong>Birthdate:</strong> {formatDate(user.dob)}</p>
            <p><strong>Anniversary:</strong> {formatDate(user.anniversaryDate)}</p>
            <p><strong>Blood Group:</strong> {user.bloodGroup?.name || '-'}</p>
            <p><strong>Gender:</strong> {user.gender || '-'}</p>
            <p><strong>House No:</strong> {user.houseNo || '-'}</p>
            <p><strong>Building / Bungalow / Society:</strong> {user.buildingName || '-'}</p>
            <p><strong>Landmark:</strong> {user.landmark || '-'}</p>
            <p><strong>Area:</strong> {user.area || '-'}</p>
            <p><strong>City:</strong> {user.city?.name || '-'}</p>
            <p><strong>State:</strong> {user.state?.name || '-'}</p>
            <p><strong>Country:</strong> {user.country?.name || '-'}</p>
            <p><strong>Pincode:</strong> {user.pincode || '-'}</p>
            <p><strong>Emergency Contact:</strong> {user.emergencyContactName || '-'} ({user.emergencyContactMobile || '-'})</p>
            <p><strong>Roles:</strong> {renderData(user.roles)}</p>
            <p><strong>Clusters:</strong> {renderData(user.cluster)}</p>
            <p><strong>Chapters:</strong> {renderData(user.chapter)}</p>
            <p><strong>Responsibilities:</strong> {renderData(user.responsibilities)}</p>
            <p><strong>Joining Date:</strong> {formatDate(user.joiningDate)}</p>
            <p>
              <strong>Invited By:</strong>{" "}
              {getInvitedByName()}
            </p>
             {/* Aadhaar Card */}
            <div className="mb-3">
              <label className="form-label">Aadhaar Card</label>
              <div>
                {user.aadharCard || user.adharCardImage ? (
                  <img
                    src={user.aadharCard || user.adharCardImage}
                    alt="Aadhaar Card"
                    style={{ width: 120, height: 80, objectFit: "cover", border: "1px solid #ccc" }}
                  />
                ) : (
                  <span className="text-muted">No image</span>
                )}
              </div>
            </div>
            {/* PAN Card */}
            <div className="mb-3">
              <label className="form-label">PAN Card</label>
              <div>
                {user.panCard || user.panCardImage ? (
                  <img
                    src={user.panCard || user.panCardImage}
                    alt="PAN Card"
                    style={{ width: 120, height: 80, objectFit: "cover", border: "1px solid #ccc" }}
                  />
                ) : (
                  <span className="text-muted">No image</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - Tabs */}
        <div className="col-md-8">
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                type="button text-primary-custom"
                className={`nav-link ${activeTab === 'business' ? 'active' : ''}`}
                onClick={() => setActiveTab('business')}
              >
                Business Info
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'children' ? 'active' : ''}`}
                onClick={() => setActiveTab('children')}
              >
                Family Info
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'activity' ? 'active' : ''}`}
                onClick={() => setActiveTab('activity')}
              >
                Activity
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'accounting' ? 'active' : ''}`}
                onClick={() => setActiveTab('accounting')}
              >
                Accounting
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'attandance' ? 'active' : ''}`}
                onClick={() => setActiveTab('attandance')}
              >
                Attandance
              </button>
            </li>
          </ul>

          {/* Business Tab */}
          {activeTab === 'business' && (
            <div className="card shadow p-3">
              <h5 className="text-primary-custom mb-3">Business Information</h5>
              {user.business && user.business.length > 0 ? (
                user.business.map((biz, idx) => (
                  <div key={idx} className="mb-4">
                    {/* Banner Image */}
                    <div className="mb-2">
                      <label className="form-label">Banner</label>
                      <div>
                        {biz.banner ? (
                          <img
                            src={biz.banner}
                            alt="Banner"
                            style={{ width: 200, height: 80, objectFit: "cover", border: "1px solid #ccc" }}
                          />
                        ) : (
                          <span className="text-muted">No image</span>
                        )}
                      </div>
                    </div>
                    <p><strong>Business Name:</strong> {biz.name || '-'}</p>
                    <p><strong>Established:</strong> {formatDate(biz.establishedDate)}</p>
                    <p><strong>Mobile:</strong> {biz.number || '-'}</p>
                    <p><strong>Type:</strong> {renderData(biz.type)}</p>
                    <p><strong>Category:</strong> {renderData(biz.category)}</p>
                    <p><strong>Sub Category:</strong> {renderData(biz.subCategory)}</p>
                    <p><strong>Formation:</strong> {renderData(biz.formation)}</p>
                    <p><strong>Team Size:</strong> {biz.teamSize || '-'}</p>
                    <p><strong>GST Number:</strong> {biz.gstNumber || '-'}</p>
                    <p><strong>Website:</strong> {biz.websiteURL || '-'}</p>
                    <p><strong>Offine No:</strong> {biz.buildingno || '-'}</p>
                    <p><strong>Building Name:</strong> {biz.buildingName || '-'}</p>
                    <p><strong>Landmark:</strong> {biz.landmark || '-'}</p>
                    <p><strong>Area:</strong> {biz.area || '-'}</p>
                    <p><strong>City:</strong> {biz.city?.name || '-'}</p>
                    <p><strong>State:</strong> {biz.state?.name || '-'}</p>
                    <p><strong>Country:</strong> {biz.country?.name || '-'}</p>
                    <p><strong>Pincode:</strong> {biz.pincode || '-'}</p>
                    <p><strong>Description:</strong> {biz.description || '-'}</p>
                    {/* Logo Image */}
                    <div className="mb-2">
                      <label className="form-label">Logo</label>
                      <div>
                        {biz.logo ? (
                          <img
                            src={biz.logo}
                            alt="Logo"
                            style={{ width: 80, height: 80, objectFit: "cover", border: "1px solid #ccc", borderRadius: "50%" }}
                          />
                        ) : (
                          <span className="text-muted">No image</span>
                        )}
                      </div>
                    </div>
                    {/* Certificate Image */}
                    <div className="mb-2">
                      <label className="form-label">Certificate</label>
                      <div>
                        {biz.gstImage ? (
                          <img
                            src={biz.gstImage}
                            alt="Certificate"
                            style={{ width: 120, height: 80, objectFit: "cover", border: "1px solid #ccc" }}
                          />
                        ) : (
                          <span className="text-muted">No image</span>
                        )}
                      </div>
                    </div>
                    <hr />
                  </div>
                ))
              ) : (
                <p>No business data available.</p>
              )}
            </div>
          )}

          {/* Activity Tab */}
          {activeTab === 'activity' && (
          <div className="card shadow p-3 w-50">
            <h5 className="text-primary-custom mb-3">Activity</h5>
            <ul className="list-group">
              {['C2C', 'Reference', 'ThankYouSlip', 'Testimonial', 'Ask'].map(type => {
                const currentMonth = new Date().getMonth() + 1; // 1 = Jan, 12 = Dec
                const showJanJun = currentMonth >= 1 && currentMonth <= 6;
                const tenureLabel = showJanJun ? "Jan-Jun" : "Jul-Dec";
                const tenureCount = showJanJun
                  ? activityTenure?.[type]?.Jan_Jun ?? 0
                  : activityTenure?.[type]?.Jul_Dec ?? 0;

                return (
                  <li key={type} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{type}</span>
                      <div>
                        <span className="badge bg-secondary rounded-pill me-2">
                          {tenureLabel}: {tenureCount}
                        </span>
                        <span className="badge bg-primary rounded-pill">
                          Total: {activity?.[type] ?? 0}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

          {/* Accounting Tab */}
         {activeTab === 'accounting' && (
        <div className="card shadow p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="text-primary-custom mb-0">Accounting</h5>
            {/* <button className="btn btn-success" onClick={() => setShowPlanModal(true)}>
              Purchase Plan
            </button> */}
          </div>
          {/* Show current plan details if available */}
          {Array.isArray(plan) && plan.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-bordered table-striped">
                <thead className="table-light">
                  <tr>
                    <th>Plan Name</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Plan Price</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.map((planObj, idx) => {
                    const planData = Array.isArray(planObj.plan) ? planObj.plan[0] : planObj.plan;
                    const isExpired = Boolean(
                      planObj.expiredAt ||
                      planObj.isExpired ||
                      (planObj.endDate && (() => {
                        const d = new Date(planObj.endDate);
                        return !isNaN(d) && d < new Date();
                      })())
                    );
                    const isProcessing = expiringPlanId === planObj._id;

                    return (
                      <tr key={planObj._id || idx}>
                        <td>{planData?.name || '-'}</td>
                        <td>{formatDate(planObj.startDate)}</td>
                        <td>{formatDate(planObj.endDate)}</td>
                        <td>₹{planObj.price || '-'}</td>
                        <td>
                          {isExpired ? (
                            <>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={async () => {
                                    const result = await confirmDelete('Are you sure you want to mark this plan as active (unexpire)?');
                                    if (!result || !result.isConfirmed) return;
                                    setExpiringPlanId(planObj._id);
                                    try {
                                      const res = await axios.put('/api/user/purchase-plan', {
                                        userId: user._id,
                                        purchasePlanId: planObj._id,
                                        isExpired: false
                                      });
                                      if (res.data && res.data.Status === 1) {
                                        toastSuccess('Plan marked as unexpired!');
                                        setPlan(prev =>
                                          prev.map(p =>
                                            p._id === planObj._id
                                              ? { ...p, ...res.data.data }
                                              : p
                                          )
                                        );
                                      } else {
                                        toastError(res.data?.Message || 'Failed to unexpire plan');
                                      }
                                    } catch (err) {
                                      toastError('Failed to unexpire plan');
                                    }
                                    setTimeout(() => setExpiringPlanId(null), 800);
                                  }}
                                disabled={isProcessing}
                              >
                                {isProcessing ? "Unexpiring..." : "Unexpire"}
                              </button>
                            </>
                          ) : (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={async () => {
                                const result = await confirmDelete('Are you sure you want to expire this plan?');
                                if (!result || !result.isConfirmed) return;
                                setExpiringPlanId(planObj._id);
                                try {
                                  const res = await axios.put('/api/user/purchase-plan', {
                                    userId: user._id,
                                    purchasePlanId: planObj._id,
                                    isExpired: true,
                                  });
                                  if (res.data && res.data.Status === 1) {
                                    toastSuccess('Plan marked as expired!');
                                    setPlan(prev =>
                                      prev.map(p =>
                                        p._id === planObj._id
                                          ? { ...p, ...res.data.data }
                                          : p
                                      )
                                    );
                                  } else {
                                    toastError(res.data?.Message || 'Failed to expire plan');
                                  }
                                } catch (err) {
                                  toastError('Failed to expire plan');
                                }
                                setTimeout(() => setExpiringPlanId(null), 800);
                              }}
                              disabled={isProcessing}
                            >
                              {isProcessing ? "Expiring..." : "Expire"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
    {showPlanModal && (
      <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.3)' }} tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Purchase Plan</h5>
              <button type="button" className="btn-close" onClick={() => setShowPlanModal(false)}></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Select Plan</label>
                <Select
                  options={planOptions}
                  value={selectedPlan}
                  onChange={setSelectedPlan}
                  placeholder="Select Plan"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={planStartDate}
                  onChange={e => setPlanStartDate(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowPlanModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-success" onClick={handleSavePlan} disabled={savingPlan}>
                {savingPlan ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
)}
          {/* Attandance Tab */}
         {activeTab === 'attandance' && (
        <div className="card shadow p-3">
          <h5 className="text-primary-custom mb-3">Attendance</h5>

          {loading ? (
            <p>Loading attendance...</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-striped">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Event Name</th>
                    <th>Event Type</th>
                    <th>Status</th>
                  </tr>
                </thead>
               <tbody>
                  {attendance.length > 0 ? (
                    attendance.map((att, idx) => (
                      <tr key={idx}>
                        <td>{formatDate(att.event?.startDate)}</td>
                        <td>{att.event?.name || '-'}</td>
                        <td>{att.event?.type && att.event.type.name ? att.event.type.name : '-'}</td>
                        <td>
                          <span
                            className={`badge ${att.status === 'present' ? 'bg-success' : 'bg-danger'}`}
                          >
                            {att.status
                              ? att.status.charAt(0).toUpperCase() + att.status.slice(1)
                              : '-'}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">No attendance records available.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
          {/* Children Tab */}
          {activeTab === 'children' && (
            <div>
              {/* Wife Info */}
              <div className="card shadow p-3 mb-4">
                <h5 className="text-primary-custom mb-3">Wife Information</h5>
                {user?.wife ? (
                  <>
                    <p><strong>Name:</strong> {user.wife.name || '-'}</p>
                    <p><strong>DOB:</strong> {user.wife.dob ? formatDate(user.wife.dob) : '-'}</p>
                    <p><strong>Mobile Number:</strong> {user.wife.mobileNumber || '-'}</p>
                  </>
                ) : (
                  <p>No wife data available.</p>
                )}
              </div>

              {/* Children Info */}
              <div className="card shadow p-3">
                <h5 className="text-primary-custom mb-3">Children</h5>
                {user?.children && user.children.length > 0 ? (
                  user.children.map((child, idx) => (
                    <div key={idx} className="mb-2">
                      <p><strong>Name:</strong> {child.name || '-'}</p>
                      <p><strong>DOB:</strong> {child.dob ? formatDate(child.dob) : '-'}</p>
                      <p><strong>Gender:</strong> {child.gender || '-'}</p>
                      <hr />
                    </div>
                  ))
                ) : (
                  <p>No child data available.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FamilyView;